import { LazyQueryExecFunction, OperationVariables } from '@apollo/client'
import { CircularProgress, Collapse, Typography } from '@material-ui/core'
import ArrowRightIcon from '@material-ui/icons/ArrowRight'
import { useState } from 'react'
import InfiniteScroll from 'react-infinite-scroller'

import { DateObject } from '../../helpers/date'
import { persianRegex } from '../../helpers/regex'
import { getTranslate } from '../../localization'
import {
  MonitoringChannel,
  MonitoringUpdateAccommodationLayer,
  MonitoringUpdateRatePlanrResponse,
  MonitoringUpdateRoomTypeLayerResponse
} from '../../types/monitoringUpdate'
import RoomTypeLayer from './RoomTypeLayer'
import useStyles from './Styles/ChannelsLayer.style'
import TimeCell from './TimeCell'

interface ChannelsLayerProps {
  range: 'TWO_WEEKS' | 'MONTH'
  channel: MonitoringChannel
  dataRoomTypeName: MonitoringUpdateAccommodationLayer
  weekendIndexes: number[]
  showChannelsLayer: boolean
  roomTypeMonitoringUpdate: MonitoringUpdateRoomTypeLayerResponse | undefined
  ratePlanMonitoringUpdate: MonitoringUpdateRatePlanrResponse | undefined
  getMonitoringRoomTypeData: LazyQueryExecFunction<
    {
      getUpdateMonitoringRoomTypeLayer: MonitoringUpdateRoomTypeLayerResponse
    },
    OperationVariables
  >
  getMonitoringRatePlanData: LazyQueryExecFunction<
    {
      getUpdateMonitoringRatePlanLayer: MonitoringUpdateRatePlanrResponse
    },
    OperationVariables
  >
  inventoryTypeAutocomplete: any
  statusAutocomplete: any
  selectedDateInDatePicker: DateObject
  propertyCode: string
  loadingForRatePlan: boolean
  loadingForRoomType: boolean
  currentIndexChannelsLayer: number
  setCurrentIndexChannelsLayer: React.Dispatch<React.SetStateAction<number>>
  channelIndex: number
}

export default function ChannelsLayer({
  range,
  channel,
  dataRoomTypeName,
  weekendIndexes,
  showChannelsLayer,
  roomTypeMonitoringUpdate,
  ratePlanMonitoringUpdate,
  getMonitoringRoomTypeData,
  getMonitoringRatePlanData,
  inventoryTypeAutocomplete,
  statusAutocomplete,
  selectedDateInDatePicker,
  propertyCode,
  loadingForRatePlan,
  loadingForRoomType,
  currentIndexChannelsLayer,
  setCurrentIndexChannelsLayer,
  channelIndex
}: ChannelsLayerProps) {
  const classes = useStyles()
  const [showRoomTypeInChannel, setShowRoomTypeInChannel] = useState(false)
  const PAGE_SIZE = 5
  const [numberOfVisibleRoomTypes, setnumberOfVisibleRoomTypes] =
    useState(PAGE_SIZE)
  const [currentIndex, setCurrentIndex] = useState<number>(-1)
  const onClickRoomTypeInChannel = (index: number) => {
    setShowRoomTypeInChannel(!showRoomTypeInChannel)
    setCurrentIndexChannelsLayer(index)
    getMonitoringRoomTypeData({
      variables: {
        filters: {
          inventoryType:
            inventoryTypeAutocomplete && inventoryTypeAutocomplete.value,
          from: selectedDateInDatePicker.gregorianStartDate,
          to: selectedDateInDatePicker.gregorianEndDate,
          updateHealthStatus: statusAutocomplete
            ? statusAutocomplete.value
            : undefined,
          propertyCode: propertyCode,
          channelID: channel.channelID
        }
      }
    })
  }
  const isPersian = persianRegex.test(channel.channelName)

  return (
    <div>
      <div
        className={[
          classes.channelsRow,
          range === 'MONTH' ? classes.channelsRowForMonth : ''
        ].join(' ')}>
        <div className={classes.channelsRowContainer}>
          <div className={classes.channelsRowIcon}>
            <div className={classes.channelsRowName}>
              <Typography
                variant="h5"
                className={classes.rowNameTitle}
                style={isPersian ? { fontFamily: 'IranSans' } : undefined}>
                {channel.channelName}
              </Typography>
              <div className={classes.textAndIcon}>
                <Typography
                  variant="h6"
                  onClick={() => onClickRoomTypeInChannel(channelIndex)}
                  className={classes.numberOfRoomType}
                  component="span">
                  {`${dataRoomTypeName.numberOfRoomType} ${getTranslate(
                    'Room Type'
                  )}`}
                </Typography>
                {loadingForRoomType &&
                channelIndex === currentIndexChannelsLayer ? (
                  <CircularProgress size={12} />
                ) : (
                  <ArrowRightIcon
                    onClick={() => onClickRoomTypeInChannel(channelIndex)}
                    className={[
                      classes.channelArrowRight,
                      showRoomTypeInChannel
                        ? classes.channelArrowRightCollapsed
                        : ''
                    ].join(' ')}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        {channel.dataForEachDate.map((dataStatus, index) => {
          return (
            <div
              key={index}
              className={[
                classes.dayContainer,
                weekendIndexes.includes(index % 7)
                  ? classes.weekendDayContainer
                  : ''
              ].join(' ')}>
              <TimeCell dataStatus={dataStatus} range={range}></TimeCell>
            </div>
          )
        })}
      </div>
      <Collapse in={showRoomTypeInChannel}>
        <div>
          <InfiniteScroll
            pageStart={0}
            loadMore={() => {
              setTimeout(() => {
                setnumberOfVisibleRoomTypes(
                  numberOfVisibleRoomTypes + PAGE_SIZE
                )
              }, 500)
            }}
            hasMore={
              roomTypeMonitoringUpdate &&
              numberOfVisibleRoomTypes <=
                roomTypeMonitoringUpdate.data.length - 1
            }
            loader={
              <div key={0} className={classes.loadingContainer}>
                <CircularProgress size={32} />
              </div>
            }
            useWindow={true}>
            {roomTypeMonitoringUpdate?.data
              .slice(0, numberOfVisibleRoomTypes)
              .map((dataRoomType, indexRoomType) => (
                <RoomTypeLayer
                  dataRoomType={dataRoomType}
                  key={indexRoomType}
                  range={range}
                  weekendIndexes={weekendIndexes}
                  indexRoomType={indexRoomType}
                  showChannelsLayer={showChannelsLayer}
                  ratePlanMonitoringUpdate={ratePlanMonitoringUpdate}
                  getMonitoringRatePlanData={getMonitoringRatePlanData}
                  inventoryTypeAutocomplete={inventoryTypeAutocomplete}
                  statusAutocomplete={statusAutocomplete}
                  selectedDateInDatePicker={selectedDateInDatePicker}
                  propertyCode={propertyCode}
                  channelID={channel.channelID}
                  loadingForRatePlan={loadingForRatePlan}
                  currentIndex={currentIndex}
                  setCurrentIndex={setCurrentIndex}
                />
              ))}
          </InfiniteScroll>
        </div>
      </Collapse>
    </div>
  )
}
